import React from 'react';
import { ScrollView, Switch, Spinner, useTheme, XStack } from 'tamagui';
import { Info, ChevronRight } from '@tamagui/lucide-icons';

import { CobuContainer, CobuStack, CobuText, CobuButton, CobuCard, CobuImage } from '@cobu/ui';
import { useCustomizeSection } from '../../hooks/useCustomizeSection';
import { URLS } from '../../constants/Urls';

// Main component
export const CustomizeSection: React.FC = () => {
  const {
    isLoading,
    isActionLoading,
    settingsError,
    showProfileImage,
    isProfileSet,
    onToggleProfileImage,
    onWelcomeMessage,
    onSetProfileImage,
    previewReferralPage,
    strings,
  } = useCustomizeSection();

  const theme = useTheme();

  // Render functions
  const renderLoadingState = () => (
    <CobuContainer f={1} bg="$white" jc="center" ai="center">
      <CobuStack ai="center" gap="$4">
        <Spinner size="large" color="$cobuTeal" />
        <CobuText variant="body" color="$cobuDarkBlue">
          {strings.loading}
        </CobuText>
      </CobuStack>
    </CobuContainer>
  );

  const renderErrorState = () => (
    <CobuContainer f={1} bg="$white" jc="center" ai="center" px="$4">
      <CobuText variant="body" color="$red" ta="center">
        {strings.errorMessage}
      </CobuText>
    </CobuContainer>
  );

  const renderDescriptionSection = () => (
    <CobuStack p="$4">
      <CobuText variant="body" fontSize="$3">
        <Info size={20} color="$cobuDarkBlue" />{' '}
        {strings.description}
        {'\n\n'}
        {strings.descriptionPrefix}{' '}
        <CobuText fontWeight="bold">
          {strings.descriptionSuffix}
        </CobuText>
      </CobuText>
    </CobuStack>
  );

  const renderPreviewSection = () => (
    <CobuCard mx="$0" mb="$0" 
    bg="$white" 
    br="$0" p="$2" px='$4'>
      <CobuButton
        onPress={previewReferralPage}
        bw={0}
        fd="row"
        jc="space-between"
        bg="$white"
        ai="center"
        p="$0"
      >
        <CobuStack layout="horizontal" ai="center" gap="$3">
          <CobuStack 
            w={48} 
            h={48} 
            br="$0" 
            ai="center" 
            jc="center"
            ov="hidden"
          >
            <CobuImage
              source={URLS.PREVIEW_PROFILE_IMAGE}
              style={{ width: 48, height: 48 }}
              containerProps={{ w: 48, h: 48 }}
            />
          </CobuStack>
          <CobuText 
            variant="body" 
            fontSize="$3" 
            fontWeight="medium" 
            color="$textLinkColor"
          >
            {strings.previewPage}
          </CobuText>
        </CobuStack>
        <ChevronRight size={20} color="$cobuDarkBlue" />
      </CobuButton>
    </CobuCard>
  );

  const renderCustomizeHeading = () => (
    <CobuStack px="$4" pt="$5" pb="$0">
      <CobuText 
        variant="body" 
        fontSize="$1" 
        fontWeight="bold" 
        color="$dusk"
      >
        {strings?.sectionTitle?.toUpperCase() || ''}
      </CobuText>
    </CobuStack>
  );

  const renderProfileImageToggle = () => (
    <CobuStack 
      layout="horizontal" 
      ai="center" 
      jc="space-between" 
      py="$0"
    >
      <CobuText variant="body" fontSize="$3" p="$2">
        {strings.includeProfilePicture}
      </CobuText>
      <XStack backgroundColor="$white" padding={2} borderRadius={9999} flexShrink={0}>
        <Switch
          size="$3"
          checked={!!showProfileImage}
          onCheckedChange={onToggleProfileImage as any}
          aria-label={strings.includeProfilePicture}
          width={47}
          height={27}
          borderRadius={9999}
          borderWidth={1}
          borderColor={!!showProfileImage ? ('$cobuTopaz' as any) : ('$colorMuted' as any)}
          backgroundColor={!!showProfileImage ? ('$cobuTopaz' as any) : ('$colorMuted' as any)}
          animation="quick"
          flexShrink={0}
          disabled={isActionLoading}
        >
          <Switch.Thumb backgroundColor="$white" width={25} height={25} borderRadius={9999} animation="quick" />
        </Switch>
      </XStack>
    </CobuStack>
  );

  const renderUpdateProfileImage = () => {
    if (isProfileSet) return null;

    return (
    <CobuButton
        onPress={onSetProfileImage}
        bg="$white"
        bw={0}
        fd="row"
        jc="space-between"
        ai="center"
        p="$0"
    >
        <CobuText variant="body" fontSize="$3">
        {strings.updateProfileImage}
        </CobuText>
        <ChevronRight size={20} color="$color" />
    </CobuButton>
    );
  };

  const renderWelcomeMessage = () => (
    <>
      {/* Separator */}
      <CobuStack h={1} bg="$borderColor" />
      
      {/* Welcome Message */}
      <CobuButton
        onPress={onWelcomeMessage}
        bg="$white"
        bw={0}
        fd="row"
        jc="space-between"
        ai="center"
        p="$0"
      >
        <CobuText variant="body" fontSize="$3" p="$2">
          {strings.welcomeMessage}
        </CobuText>
        <ChevronRight size={20} color="$color" />
      </CobuButton>
    </>
  );

  const renderCustomizeOptions = () => (
    <CobuCard mx="$0" mt="$0" bg="$white">
      <CobuStack gap="$0">
        {renderProfileImageToggle()}
        {renderUpdateProfileImage()}
        {renderWelcomeMessage()}
      </CobuStack>
    </CobuCard>
  );

  const renderLoadingOverlay = () => {
    if (!isActionLoading) return null;

    return (
      <CobuStack pos="absolute" t={0} l={0} r={0} b={0} jc="center" ai="center" bg="$blackTransparent50">
        <Spinner size="large" color="$white" />
      </CobuStack>
    );
  };

  // Main render
  if (isLoading) {
    return renderLoadingState();
  }

  return (
    <CobuContainer f={1} bg="$white">
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Description Section */}
        {renderDescriptionSection()}

        {/* Combined Preview and Customize Section */}
        <CobuStack bc="$pageGrey" py="$4">
          {renderPreviewSection()}
          {renderCustomizeHeading()}
          {renderCustomizeOptions()}
        </CobuStack>

        {/* Loading overlay */}
        {renderLoadingOverlay()}
      </ScrollView>
    </CobuContainer>
  );
};
